import type { CurrentAccountUser, PermissionsLocalized } from 'src/api/main'

import { isWorkspacePathAllowedByPermissions } from './workspacePermissions'

/**
 * Workspace sidebar keys. Which of them a member may open is decided by their
 * effective permissions — see `WORKSPACE_MENU_PERMISSIONS` in
 * `workspacePermissions.ts`. This file owns the other axis: whether the
 * signed-in profile belongs to the workspace at all.
 */
export const WORKSPACE_MENU_KEYS = [
  'home',
  'overview',
  'users',
  'organization',
  'roles',
  'security',
  'workspaceSettings',
  'domains',
  'publicAccess',
  'aiConnectors',
  'billing',
  'apps',
  'agents',
  'audit',
  'companyGraph'
] as const

/**
 * The workspace tier the session reports for this workspace (`owner`, `admin`,
 * …). No longer used for gating — permissions do that — but ownership is still
 * a distinct fact some screens need (e.g. "you are the owner" notices).
 */
export function resolveWorkspaceAccessRoleFromSession(
  user: CurrentAccountUser | undefined,
  workspaceId: number
): string | undefined {
  return user?.organizations?.find((o) => o.id === workspaceId)?.access_role
}

/** True when the session's profile is the workspace owner. */
export function isWorkspaceOwnerFromSession(
  user: CurrentAccountUser | undefined,
  workspaceId: number
): boolean {
  return (
    resolveWorkspaceAccessRoleFromSession(user, workspaceId)
      ?.trim()
      .toLowerCase() === 'owner'
  )
}

/**
 * Whether the signed-in profile belongs to this workspace at all. This is the
 * membership axis, checked before any permission check: a profile with no
 * `OrganizationAccess` row for this workspace is not a member, and gets the
 * "wrong account" screen rather than an empty permission list.
 */
export function isWorkspaceMemberOfSession(
  user: CurrentAccountUser | undefined,
  workspaceId: number
): boolean {
  return user?.organizations?.some((o) => o.id === workspaceId) ?? false
}

export type WorkspaceAccessStatus = 'no-access' | 'insufficient-role' | 'ok'

/**
 * Resolves whether the signed-in profile can render the given workspace path.
 * Membership comes from the session; the per-section check comes from the
 * effective permissions (`GET /organizations/permissions/user/`).
 *
 * Pass `permissions` only once resolved — an `undefined` list denies every
 * gated section, so callers must render a loading state until it arrives.
 */
export function resolveWorkspaceAccessStatus(
  user: CurrentAccountUser | undefined,
  workspaceId: number,
  pathname: string,
  permissions: PermissionsLocalized[] | undefined
): WorkspaceAccessStatus {
  if (!isWorkspaceMemberOfSession(user, workspaceId)) {
    return 'no-access'
  }

  return isWorkspacePathAllowedByPermissions(pathname, permissions)
    ? 'ok'
    : 'insufficient-role'
}
